import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { BrandMark } from "./BrandMark";
import { Coins, ShieldCheck, Menu, X } from "lucide-react";

/**
 * Header variants:
 *   "light"  — original ivory/peach editorial chrome (used by dashboard, builder, app pages)
 *   "dark"   — cinematic black with burnished gold accents (new marketing surface)
 */
export const Header = ({ variant = "light" }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const dark = variant === "dark";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { to: "/templates", label: "Templates", testid: "nav-templates" },
    { to: "/pricing", label: "Pricing", testid: "nav-pricing" },
    { to: "/faq", label: "FAQ", testid: "nav-faq" },
  ];

  // Style tokens per variant
  const surfaceBg = dark
    ? scrolled
      ? "bg-matte-black/85 backdrop-blur-xl border-b border-warm-ivory/10"
      : "bg-matte-black/40 backdrop-blur-md"
    : scrolled
      ? "bg-cream/95 backdrop-blur-md border-b border-ink/10"
      : "bg-cream/80 backdrop-blur-sm";

  const linkClass = dark
    ? "text-[12px] font-mono uppercase tracking-[0.18em] text-warm-ivory/65 hover:text-burnished-gold transition-colors"
    : "text-[13px] font-mono uppercase tracking-[0.18em] text-ink/70 hover:text-oxblood transition-colors";

  const brandText = dark
    ? "font-display text-[1.4rem] tracking-tight font-medium leading-none text-warm-ivory hidden sm:inline"
    : "font-display text-[1.45rem] tracking-tight font-medium leading-none hidden sm:inline";

  const brandItalic = dark ? "italic text-warm-ivory" : "italic text-[var(--accent-deep)]";
  const signoutClass = dark
    ? "text-xs font-mono uppercase tracking-widest text-warm-ivory/70 hover:bg-transparent hover:text-burnished-gold"
    : "text-xs font-mono uppercase tracking-widest hover:bg-transparent hover:text-oxblood";

  const ctaBtn = dark
    ? "btn-gold rounded-full px-6 h-10 font-mono uppercase tracking-[0.16em] text-[11px]"
    : "btn-accent rounded-full px-6 h-10 font-mono uppercase tracking-[0.16em] text-[11px]";

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-colors duration-300 ${surfaceBg}`}
    >
      <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between gap-3">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
          <BrandMark variant={dark ? "dark" : "light"} size={40} className="shrink-0" />
          <span className={brandText}>
            Prestique<span className={brandItalic}>Resume</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {navLinks.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid} className={linkClass}>
              {l.label}
            </Link>
          ))}
          {user && (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard" className={linkClass}>Dashboard</Link>
              <Link to="/cover-letters" data-testid="nav-letters" className={linkClass}>Letters</Link>
              <Link to="/payslips" data-testid="nav-payslips" className={linkClass}>Payslips</Link>
              {user.is_admin && (
                <Link to="/admin" data-testid="nav-admin" className={`${linkClass} flex items-center gap-1`}>
                  <ShieldCheck className="w-3.5 h-3.5" /> Admin
                </Link>
              )}
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              {user.plan_tier === "free" ? (
                <Link to="/pricing" data-testid="nav-upgrade" className="hidden sm:inline-flex">
                  <Button className={ctaBtn}>
                    <Coins className="w-3.5 h-3.5 mr-1.5" /> Upgrade
                  </Button>
                </Link>
              ) : (
                <Link
                  to="/pricing"
                  data-testid="credit-pill"
                  className={`hidden sm:inline-flex items-center gap-1.5 text-[11px] px-3 h-8 rounded-full font-mono uppercase tracking-widest ${dark ? "bg-burnished-gold/15 text-burnished-gold border border-burnished-gold/30" : "bg-ink text-cream"}`}
                >
                  <Coins className="w-3 h-3" />
                  {user.plan_tier === "admin_unlimited" ? "Unlimited" : `${user.credits} credits`}
                </Link>
              )}
              <Button
                data-testid="logout-btn"
                onClick={() => { logout(); navigate("/"); }}
                variant="ghost"
                className={signoutClass}
              >
                Sign out
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className={`hidden sm:inline ${linkClass}`}>
                Sign in
              </Link>
              <Link to="/signup" data-testid="nav-signup">
                <Button className={ctaBtn}>Get started</Button>
              </Link>
            </>
          )}

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-btn"
            className={`md:hidden p-2 -mr-2 ${dark ? "text-warm-ivory" : "text-ink"}`}
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className={`md:hidden border-t ${dark ? "border-warm-ivory/10 bg-matte-black" : "border-ink/10 bg-cream"}`} data-testid="mobile-menu">
          <div className="px-5 py-6 flex flex-col gap-4">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className={`font-mono uppercase tracking-widest text-sm ${dark ? "text-warm-ivory/80" : "text-ink/80"}`}>{l.label}</Link>
            ))}
            {user && (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className={`font-mono uppercase tracking-widest text-sm ${dark ? "text-warm-ivory/80" : "text-ink/80"}`}>Dashboard</Link>
                <Link to="/cover-letters" onClick={() => setOpen(false)} className={`font-mono uppercase tracking-widest text-sm ${dark ? "text-warm-ivory/80" : "text-ink/80"}`}>Letters</Link>
                <Link to="/payslips" onClick={() => setOpen(false)} className={`font-mono uppercase tracking-widest text-sm ${dark ? "text-warm-ivory/80" : "text-ink/80"}`}>Payslips</Link>
              </>
            )}
            {!user && (
              <Link to="/login" onClick={() => setOpen(false)} className={`font-mono uppercase tracking-widest text-sm ${dark ? "text-warm-ivory/80" : "text-ink/80"}`}>Sign in</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
